"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import Image from "next/image";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { id: "nav_home", label: "Home", href: "/homepage" },
    { id: "nav_about", label: "About Us", href: "/about" },
    { id: "nav_services", label: "Services", href: "/services" },
    { id: "nav_properties", label: "Properties", href: "/properties" },
    { id: "nav_contact", label: "Contact", href: "/contact" },
    { id: "nav_portal", label: "Client Portal", href: "/portal/login", isPortal: true },
    { id: "nav_connect", label: "Follow Us !", href: "https://avely.me/artienterprise", isSpecial: true },
  ];

  return (
    <>
      <style jsx>{`
        @keyframes gradientBlink {
          0%, 100% {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 25%, #ef4444 50%, #dc2626 75%, #fbbf24 100%);
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.6);
          }
          50% {
            background: linear-gradient(135deg, #dc2626 0%, #ef4444 25%, #f59e0b 50%, #fbbf24 75%, #dc2626 100%);
            box-shadow: 0 0 30px rgba(239, 68, 68, 0.8);
          }
        }

        @keyframes cuteClick {
          0% {
            transform: scale(1);
          }
          25% {
            transform: scale(0.85) rotate(-5deg);
          }
          50% {
            transform: scale(1.15) rotate(5deg);
          }
          75% {
            transform: scale(0.95) rotate(-2deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.6),
                        0 0 40px rgba(239, 68, 68, 0.4),
                        inset 0 0 20px rgba(255, 255, 255, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(239, 68, 68, 0.8),
                        0 0 60px rgba(251, 191, 36, 0.6),
                        inset 0 0 30px rgba(255, 255, 255, 0.3);
          }
        }

        .connect-tab {
          animation: gradientBlink 2s ease-in-out infinite, sparkle 2s ease-in-out infinite;
          background-size: 200% 200%;
          color: #dc2626 !important;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          font-weight: 700;
          transition: transform 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .connect-tab:hover {
          transform: scale(1.05);
          animation: gradientBlink 2s ease-in-out infinite, sparkle 2s ease-in-out infinite;
        }

        .connect-tab:active {
          animation: cuteClick 0.6s ease-in-out, gradientBlink 2s ease-in-out infinite, sparkle 2s ease-in-out infinite;
        }

        .connect-tab-mobile {
          animation: gradientBlink 2s ease-in-out infinite, sparkle 2s ease-in-out infinite;
          background-size: 200% 200%;
          color: #dc2626 !important;
          padding: 0.75rem 1.25rem;
          border-radius: 0.5rem;
          font-weight: 700;
          text-align: center;
          transition: transform 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .connect-tab-mobile:hover {
          transform: scale(1.02);
          animation: gradientBlink 2s ease-in-out infinite, sparkle 2s ease-in-out infinite;
        }

        .connect-tab-mobile:active {
          animation: cuteClick 0.6s ease-in-out, gradientBlink 2s ease-in-out infinite, sparkle 2s ease-in-out infinite;
        }
      `}</style>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-md"
            : "bg-white border-b border-gray-200"
        }`}
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link href="/homepage" className="flex items-center gap-3">
              <Image
                src="/assets/images/Website_logo-1769951970593.png"
                alt="ARTI Enterprise Logo"
                width={48}
                height={48}
                className="h-12 w-12 object-contain"
                priority
              />
              <div className="flex flex-col">
                <span className="text-lg font-bold text-foreground leading-tight">ARTI Enterprise</span>
                <span className="text-xs text-muted-foreground">Foundation Se Finishing Tak!</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks?.map((link) => (
                <Link
                  key={link?.id}
                  href={link?.href}
                  className={link?.isSpecial ? "connect-tab" : link?.isPortal ? "flex items-center gap-1.5 text-sm font-semibold text-yellow-600 hover:text-yellow-500 transition-colors border border-yellow-300 px-3 py-1.5 rounded-lg hover:bg-yellow-50" : `text-sm font-medium tracking-wide transition-colors ${
                    pathname === link?.href
                      ? "text-primary" :"text-foreground hover:text-primary"
                  }`}
                >
                  {link?.isPortal && (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                  {link?.label}
                </Link>
              ))}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+917890369000"
                className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-accent transition-colors flex items-center gap-2"
              >
                <Icon name="PhoneIcon" size={18} />
                Call Now
              </a>
              <a
                href="https://wa.link/yurmqd"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-medium text-sm hover:bg-yellow-500 transition-colors flex items-center gap-2"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={18} />
                WhatsApp
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-foreground hover:text-primary transition-colors"
              aria-label="Toggle menu"
            >
              <Icon
                name={isMobileMenuOpen ? "XMarkIcon" : "Bars3Icon"}
                size={28}
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navLinks?.map((link) => (
                <Link
                  key={link?.id}
                  href={link?.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={link?.isSpecial ? "connect-tab-mobile" : link?.isPortal ? "flex items-center gap-2 text-base font-semibold py-2 text-yellow-600 hover:text-yellow-500 transition-colors" : `text-base font-medium py-2 transition-colors ${
                    pathname === link?.href
                      ? "text-primary" :"text-foreground hover:text-primary"
                  }`}
                >
                  {link?.label}
                </Link>
              ))}
              <div className="flex flex-col gap-3 mt-4">
                <a
                  href="tel:+917890369000"
                  className="px-5 py-3 bg-primary text-primary-foreground rounded-lg font-medium text-center hover:bg-accent transition-colors flex items-center justify-center gap-2"
                >
                  <Icon name="PhoneIcon" size={20} />
                  Call Now
                </a>
                <a
                  href="https://wa.link/yurmqd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 bg-secondary text-secondary-foreground rounded-lg font-medium text-center hover:bg-yellow-500 transition-colors flex items-center justify-center gap-2"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={20} />
                  WhatsApp
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
      {/* Spacer to prevent content from hiding under fixed header */}
      <div className="h-20"></div>
    </>
  );
};

export default Header;